import { useEffect, useState, useCallback } from "react";
import StaffShell from "../components/StaffShell";
import { T, useI18n } from "../lib/i18n";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { MOTIF_NAMES } from "../lib/motif";
import { toast } from "sonner";
import { Music, Sparkles, PlayCircle, Check, X, Wine, Megaphone } from "lucide-react";
import { formatTimestamp } from "../lib/time";
import MomentsComposer from "../components/MomentsComposer";
import PhotoQueuePanel from "../components/PhotoQueuePanel";
import PhotobookPanel from "../components/PhotobookPanel";

export default function CoupleDash() {
  useI18n();
  const { eventId, motif, setMotif } = useMotif();
  const [music, setMusic] = useState([]);
  const [allReqs, setAllReqs] = useState([]);
  const [cocktails, setCocktails] = useState([]);

  const refresh = useCallback(async () => {
    const [m, r, c] = await Promise.allSettled([
      api.get("/music"),
      api.get("/requests/food-drink"),
      api.get("/cocktails"),
    ]);
    if (m.status === "fulfilled") setMusic(m.value.data);
    // Phase 3B: drinks are venue-handled; ignore any legacy drink rows for stats
    if (r.status === "fulfilled") setAllReqs(r.value.data.filter((x) => x.category !== "drink"));
    if (c.status === "fulfilled") setCocktails(c.value.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, () => refresh());

  const setMusicStatus = async (id, status) => {
    try { await api.put(`/music/${id}/status`, { status }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const launchGame = async (id) => {
    try { await api.put(`/games/${id}/launch`); toast.success("Game launched"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const nextQ = async (id) => {
    try { await api.put(`/games/${id}/next`); refresh(); } catch (e) { toast.error(formatErr(e)); }
  };
  const announceCocktail = async (cid, name) => {
    try {
      await api.post(`/cocktails/${cid}/announce`);
      toast.success(`Announced: ${name}`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totals = {
    pending: allReqs.filter((r) => r.status === "new").length,
    delivered: allReqs.filter((r) => r.status === "delivered").length,
  };

  return (
    <StaffShell title={T("The couple's view")} subtitle={T("Steer the night")}>
      {/* Stats + motif */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-elev p-5">
          <div className="text-[10px] tracking-luxe text-motif-muted">{T("orders pending")}</div>
          <div className="font-display text-4xl text-primary-motif">{totals.pending}</div>
        </div>
        <div className="card-elev p-5">
          <div className="text-[10px] tracking-luxe text-motif-muted">{T("orders delivered")}</div>
          <div className="font-display text-4xl text-primary-motif">{totals.delivered}</div>
        </div>
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="w-3.5 h-3.5 text-primary-motif" /><div className="text-[10px] tracking-luxe text-motif-muted">{T("motif")}</div></div>
          <div className="font-display text-2xl text-motif">{motif}</div>
          <select
            className="input-motif text-sm mt-2"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            data-testid="motif-select"
          >
            {MOTIF_NAMES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <MomentsComposer />
        <PhotoQueuePanel />
        <PhotobookPanel />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Cocktail control */}
        <div className="card-elev p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Wine className="w-4 h-4 text-primary-motif" />
            <div className="font-display text-xl">{T("Signature cocktails")}</div>
            <span className="ml-auto text-[10px] tracking-luxe text-motif-muted">{T("tap to push to all guests")}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cocktails.map((c) => (
              <div key={c.id} className="p-4 rounded-2xl border border-motif" data-testid={`couple-cocktail-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-display text-xl text-motif">{c.name}</div>
                    {c.description && <div className="text-xs text-motif-muted mt-1">{c.description}</div>}
                    {(c.ingredients || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.ingredients.slice(0, 4).map((ing, i) => (
                          <span key={`${ing}-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-accent-motif text-motif-muted">
                            {ing}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] tracking-luxe text-motif-muted mt-2">
                      announced {c.announced_count || 0}× {c.last_announced_at ? `· last ${formatTimestamp(c.last_announced_at)}` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => announceCocktail(c.id, c.name)}
                    className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 flex-shrink-0"
                    data-testid={`announce-cocktail-${c.id}`}
                  >
                    <Megaphone className="w-3.5 h-3.5" />Announce
                  </button>
                </div>
              </div>
            ))}
            {cocktails.length === 0 && (
              <div className="text-motif-muted text-sm">{T("No signature cocktails configured.")}</div>
            )}
          </div>
        </div>

        {/* Music approval */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><Music className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("DJ queue")}</div></div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {music.map((m) => (
              <div key={m.id} className="p-3 rounded-xl bg-accent-motif" data-testid={`couple-music-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-base text-motif">{m.title}</div>
                    <div className="text-[10px] text-motif-muted">{m.artist || "—"} · {m.vote_count} votes · {m.status}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setMusicStatus(m.id, "approved")} className="btn-ghost text-xs"><Check className="w-3 h-3" /></button>
                    <button onClick={() => setMusicStatus(m.id, "rejected")} className="btn-ghost text-xs"><X className="w-3 h-3" /></button>
                    <button onClick={() => setMusicStatus(m.id, "playing")} className="btn-primary text-xs px-3 py-1.5">{T("Play")}</button>
                  </div>
                </div>
              </div>
            ))}
            {music.length === 0 && <div className="text-motif-muted text-sm">{T("No music yet.")}</div>}
          </div>
        </div>

        {/* Projector shortcut */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><PlayCircle className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Projector")}</div></div>
          <a href={`/projector/${eventId}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs">{T("Open projector")}</a>
        </div>
      </div>
    </StaffShell>
  );
}
